// ===========================================================================
// Messages between regulars, client side: the thread view inside the
// regulars panel.
//
// A thread is two people who both waved. It shows the messages the server
// lists (newest at the bottom, yours on the right), a composer with the
// letter toggle and the day's cap, and -- before the first message to
// someone -- a safety card that says the two things worth knowing: they see
// your nickname, and nothing here is hidden from Lumi. The first message is a
// "message request" in the copy, because that is what it is: they can
// answer, ignore it, or block you, and you only ever see an answer.
//
// There is no read receipt and no typing indicator here, for anyone. The
// view knows when YOU last looked (the server's unread count), never when
// they did. Mounted from ui/regulars.js; app.js routes MSG, MSG_LIST,
// MSG_DELETED, and the scoped SYSTEM and ERROR frames to it.
//
// The same view also shows the conversation with Lumi Staff ("staff mode",
// opened with a regular-shaped { personId: STAFF_PERSON, handle, staff: true }).
// That thread is not a regular's: it needs no wave, ignores the band and the
// switches, and is read and written over HTTP (h.staff), not the socket. So in
// staff mode there is no letter toggle, no daily cap, no message-request card,
// and nothing to report or delete -- the person can only answer. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { createEmojiPicker } from './emoji.js';
import { el } from '../dom.js';
import { ReportReason, STAFF_NAME, STAFF_INBOX_MAX_CHARS } from '@lumi/shared';

/** The thread's empty state: the first message is a request. */
export const MESSAGE_REQUEST_COPY = 'Send a message request. They can answer, ignore it, or block you, and you will only ever see an answer.';
/** The card above the composer before your first message to someone. */
export const SAFETY_CARD_COPY = 'They see your nickname, not your name. Nothing here is encrypted from Lumi, so it can be reviewed if reported. Block or report from any message.';
/** The slow lane's toggle. */
export const LETTER_TOGGLE_COPY = 'Send as a letter (arrives in 12 hours)';
/** New conversations a day; the server's number, mirrored for the cap line. */
export const MAX_NEW_CONVERSATIONS = 5;
/** localStorage: the people whose safety card this browser has already shown. */
export const CARD_STORAGE_KEY = 'lumi.messageCards';
/** A page from the server carries this many; a full page means there may be earlier ones. */
export const THREAD_PAGE = 50;
/** What a thread with Lumi Staff says when it holds no lines. It always holds at least one, so this is only a fallback. */
export const STAFF_EMPTY_COPY = 'No messages from Lumi Staff yet.';

const REPORT_REASONS = [
  [ReportReason.NUDITY, 'Nudity or sexual content'],
  [ReportReason.MINOR_SAFETY, 'A minor is in danger'],
  [ReportReason.HARASSMENT, 'Harassment or bullying'],
  [ReportReason.VIOLENCE, 'Violence'],
  [ReportReason.HATE, 'Hate speech'],
  [ReportReason.SPAM, 'Spam'],
  [ReportReason.OTHER, 'Something else'],
];

/** "2 of 5 new conversations today". */
export function capLine(cap) {
  const n = Math.max(0, Number(cap?.newToday) || 0);
  const max = Number(cap?.max) || MAX_NEW_CONVERSATIONS;
  return `${n} of ${max} new conversation${max === 1 ? '' : 's'} today`;
}

/**
 * The small mark on a letter. Relative, not a clock time: it reads the same
 * in every time zone, and "in 11 h" is what a person wants to know. Once it
 * has arrived (or for a letter you received) it is simply "a letter".
 * @param {string} deliverAt  ISO
 * @param {number} [nowMs]
 */
export function formatArrives(deliverAt, nowMs = Date.now()) {
  const ms = Date.parse(deliverAt || '');
  if (!Number.isFinite(ms) || ms <= nowMs) return 'a letter';
  const mins = Math.floor((ms - nowMs) / 60_000);
  if (mins < 1) return 'letter, arrives any moment';
  if (mins < 60) return `letter, arrives in ${mins} min`;
  return `letter, arrives in ${Math.round(mins / 60)} h`;
}

/**
 * When a message was written: the time of day today, else the date and the time. A time
 * zone can be passed for tests; the browser's own is the default.
 * @param {string} iso
 * @param {{ nowMs?: number, timeZone?: string }} [opts]
 */
export function formatWhen(iso, { nowMs = Date.now(), timeZone } = {}) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const now = new Date(nowMs);
  const dayOpts = { year: 'numeric', month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) };
  const sameDay = d.toLocaleDateString('en-US', dayOpts) === now.toLocaleDateString('en-US', dayOpts);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
  if (sameDay) return time;
  // Another day: the date AND the time. It was the date alone, so a thread
  // read back the next morning said "Sep 29" on every line and no time on
  // any of them (the operator, 2026-09-30).
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) })}, ${time}`;
}

/**
 * What an ERROR means in the thread, or null when it is not ours to show.
 * `not_regulars` is deliberately the same sentence for a stranger and for a
 * block: the server never says which, and neither does this.
 * @param {{ code?: string, message?: string }} m
 */
export function errorText(m) {
  if (!m) return null;
  switch (m.code) {
    case 'not_regulars':
      return 'You are not regulars, so this was not sent.';
    case 'daily_cap':
      return m.message || 'You have started five new conversations today. Tomorrow is another day.';
    case 'rate_limited':
      return 'Slow down a moment.';
    case 'not_available':
      return 'Messages are not available right now.';
    case 'bad_message':
      return m.message || 'That could not be sent.';
    default:
      return null;
  }
}

/**
 * What a refused or failed message to Lumi Staff means in the thread. The codes
 * are the server's (shared/staff-inbox.js); anything else is simply "try again".
 * @param {string} [code]
 */
export function staffErrorText(code) {
  switch (code) {
    case 'rate_limited':
      return 'Slow down a moment.';
    case 'no_thread':
      return 'Only Lumi Staff can start this conversation.';
    case 'not_read':
      return 'Open it first; then it can be hidden.';
    default:
      return 'That did not go through. Try again.';
  }
}

function readCards() {
  try {
    const raw = globalThis.localStorage?.getItem(CARD_STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Whether this browser has shown the safety card for a person. */
export function hasSeenCard(personId) {
  return !!personId && readCards().includes(personId);
}

/** Remember that it was shown. A storage that throws (a private window) just means the card shows again. */
export function rememberCard(personId) {
  if (!personId) return false;
  try {
    const list = readCards();
    if (!list.includes(personId)) list.push(personId);
    globalThis.localStorage?.setItem(CARD_STORAGE_KEY, JSON.stringify(list.slice(-200)));
    return true;
  } catch {
    return false;
  }
}

/**
 * Build the thread view. Hidden until open(); one per panel.
 * @param {object} h
 * @param {() => void} h.onBack
 * @param {(withPersonId: string, before?: string) => void} h.onList
 * @param {(to: string, text: string, id: string, slow: boolean) => void} h.onSend
 * @param {(id: string) => void} h.onDelete
 * @param {(id: string, reason: string) => void} h.onReport
 * @param {() => string} h.newId
 * @param {{ list: () => Promise<{ ok: boolean, messages?: object[] }>, send: (text: string, id: string) => Promise<{ ok: boolean, message?: object, error?: string }> }|null} [h.staff]
 *   the HTTP calls for the conversation with Lumi Staff; without them a staff thread cannot be read or answered
 * @returns {{ root: HTMLElement, api: object }}
 */
export function createThreadView(h) {
  let current = null; // the regular's personId while a thread is open
  let staffMode = false; // the open thread is the one with Lumi Staff: HTTP, no letters, no cap, no report
  let staffOpening = 0; // which opening of a thread this is; an answer to an earlier one is dropped
  let handle = null;
  let pending = null; // the client id of the message we are waiting to see echoed
  let pendingTimer = null;
  let oldest = null; // the `at` of the oldest message shown, for paging
  let paging = false;
  let cardShowing = false;

  const title = el('strong', { class: 'mt-title' });
  // Built here, never taken from a name, so it stays as unforgeable as the chat's.
  const staffBadge = el('span', { class: 'staff-badge', title: t('Verified Lumi staff'), text: t('LUMI STAFF') });
  staffBadge.hidden = true;
  const list = el('div', { class: 'mt-list', role: 'log', 'aria-live': 'polite' });
  const empty = el('p', { class: 'mt-empty muted', text: MESSAGE_REQUEST_COPY });
  const older = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-xs mt-older',
    text: t('Earlier messages'),
    onClick: () => {
      if (!current || !oldest) return;
      paging = true;
      h.onList?.(current, oldest);
    },
  });
  older.hidden = true;
  const status = el('div', { class: 'mt-status muted', role: 'status' });
  const cardOk = el('button', {
    type: 'button',
    class: 'btn btn-primary btn-sm',
    text: 'OK',
    onClick: () => {
      rememberCard(current);
      showCard(false);
      input.focus();
    },
  });
  const card = el('div', { class: 'mt-card' }, [el('p', { text: SAFETY_CARD_COPY }), cardOk]);
  card.hidden = true;

  // Enter sends where there is a keyboard with a Shift key to hand; on a
  // phone it makes a new line, and the Send button sends, which is what
  // every messaging app there does. Without a matchMedia (a test) it sends.
  const enterSends = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(hover: hover)').matches : true);
  const input = el('textarea', {
    class: 'mt-input',
    rows: '1',
    maxlength: '4000',
    placeholder: t('Write a message'),
    'aria-label': 'Your message',
    onKeydown: (e) => {
      if (e.key === 'Enter' && !e.shiftKey && enterSends()) {
        e.preventDefault();
        send();
      }
    },
    onInput: () => autosize(),
  });
  // The box grows with its text, one line to about six, rather than making
  // somebody scroll inside two rows on a phone.
  function autosize() {
    if (!input.style) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight || 0, 160)}px`;
  }
  // The same picker as the chat, with the ones picked lately on top.
  const emoji = createEmojiPicker(input);
  const letter = el('input', { type: 'checkbox', class: 'mt-letter-box', id: 'mtLetter' });
  const letterLabel = el('label', { class: 'mt-letter', for: 'mtLetter' }, [letter, el('span', { text: LETTER_TOGGLE_COPY })]);
  const capEl = el('span', { class: 'mt-cap muted', text: capLine(null) });
  const sendBtn = el('button', { type: 'button', class: 'btn btn-primary btn-sm mt-send', text: t('Send'), onClick: () => send() });
  const composer = el('div', { class: 'mt-composer' }, [
    emoji.panel,
    input,
    el('div', { class: 'mt-composer-row' }, [emoji.button, letterLabel, capEl, sendBtn]),
  ]);

  const root = el('div', { class: 'msg-thread-view' }, [
    el('div', { class: 'mt-head' }, [
      el('button', { type: 'button', class: 'btn btn-ghost btn-sm mt-back', text: t('Back'), onClick: () => close(true) }),
      title,
      staffBadge,
    ]),
    older,
    list,
    empty,
    card,
    status,
    composer,
  ]);
  root.hidden = true;

  function setStatus(text) {
    status.textContent = text || '';
  }

  function showCard(on) {
    cardShowing = !!on;
    card.hidden = !on;
    sendBtn.disabled = on;
  }

  function settle() {
    pending = null;
    clearTimeout(pendingTimer);
    pendingTimer = null;
    sendBtn.disabled = cardShowing;
  }

  function isMine(m) {
    // Staff says who wrote a line outright; with a regular the view never
    // knows its own personId, and the other person's is enough.
    if (staffMode) return m.from === 'me';
    return m.from !== current;
  }

  function bubble(m) {
    const mine = isMine(m);
    const meta = [el('span', { class: 'mt-when', text: formatWhen(m.at) })];
    if (m.slow) meta.push(el('span', { class: 'mt-letter-mark', text: mine ? formatArrives(m.deliverAt) : 'a letter' }));
    // Lumi Staff's lines are not deleted or reported from here: this is the
    // conversation with the people who would read the report. Once read, a
    // line can be HIDDEN: it leaves this person's view (and, when it was the
    // last, the row in their list goes too), and stays in the console's.
    if (staffMode) {
      const hide = m.id && typeof h.staff?.hide === 'function'
        ? el('button', {
            type: 'button',
            class: 'msg-act',
            text: t('Hide'),
            title: mine ? t('Hide this message from your view') : t('Hide this message now you have read it. Lumi Staff can still see it.'),
            onClick: () => hideStaffLine(m.id),
          })
        : null;
      // staff-line: its Hide is always shown, not only on hover (styles.css): a
      // person who has read it has to be able to see how to put it away.
      return el('div', { class: `mt-msg staff-line ${mine ? 'mine' : 'theirs from-staff'}`, dataset: { mid: m.id, mine: mine ? '1' : '' } }, [
        el('div', { class: 'mt-text', text: m.text }),
        el('div', { class: 'mt-meta' }, meta),
        hide ? el('div', { class: 'mt-actions' }, [hide]) : null,
      ].filter(Boolean));
    }
    const actions = [
      el('button', { type: 'button', class: 'msg-act', text: t('Delete'), title: t('Delete this message for you'), onClick: () => h.onDelete?.(m.id) }),
    ];
    if (!mine) {
      actions.push(
        el('button', {
          type: 'button',
          class: 'msg-act danger',
          text: t('Report'),
          title: t('Report this message to the Lumi team'),
          onClick: () => {
            const idx = Number(window.prompt(`Report this message. Type the number:\n${REPORT_REASONS.map(([, label], i) => `${i + 1}. ${label}`).join('\n')}`, '')) - 1;
            if (Number.isInteger(idx) && REPORT_REASONS[idx]) h.onReport?.(m.id, REPORT_REASONS[idx][0]);
          },
        }),
      );
    }
    return el('div', { class: `mt-msg ${mine ? 'mine' : 'theirs'}`, dataset: { mid: m.id, mine: mine ? '1' : '' } }, [
      el('div', { class: 'mt-text', text: m.text }),
      el('div', { class: 'mt-meta' }, meta),
      el('div', { class: 'mt-actions' }, actions),
    ]);
  }

  function refreshEmpty() {
    const none = !list.children.length;
    empty.hidden = !none;
    if (staffMode) return; // nobody is being asked anything: there is no message request to explain
    if (!none) showCard(false);
    else if (current && !hasSeenCard(current)) showCard(true);
  }

  function scrollDown() {
    list.scrollTop = list.scrollHeight;
  }

  function send() {
    if (!current || pending || cardShowing) return;
    const text = input.value.trim();
    if (!text) return;
    pending = h.newId ? h.newId() : String(Date.now());
    sendBtn.disabled = true;
    emoji.close();
    setStatus('');
    if (staffMode) {
      sendToStaff(text, pending);
      return;
    }
    h.onSend?.(current, text, pending, letter.checked);
    // If nothing comes back -- the socket dropped -- the composer is not stuck.
    pendingTimer = setTimeout(() => {
      if (pending) {
        settle();
        setStatus('That did not go through. Try again.');
      }
    }, 8000);
  }

  /**
   * A reply to Lumi Staff, over HTTP: the answer is the echo, so the line goes
   * in when the POST returns. A refusal is said under the composer and the
   * words are kept, as a refusal is for a regular.
   */
  function sendToStaff(text, id) {
    const opening = staffOpening;
    const done = (r) => {
      if (pending !== id || opening !== staffOpening) return; // the thread was left or reopened meanwhile
      settle();
      if (!r || !r.ok || !r.message) {
        setStatus(staffErrorText(r?.error));
        return;
      }
      list.append(bubble({ ...r.message, from: 'me' }));
      scrollDown();
      input.value = '';
      autosize();
      refreshEmpty();
    };
    if (typeof h.staff?.send !== 'function') {
      done({ ok: false, error: 'failed' });
      return;
    }
    Promise.resolve(h.staff.send(text, id)).then(done, () => done({ ok: false, error: 'failed' }));
  }

  /**
   * Put a line with Lumi Staff away. The bubble goes when the server agrees;
   * a thread left with nothing in it closes, back to the list, where the row
   * goes too.
   */
  function hideStaffLine(id) {
    const opening = staffOpening;
    const node = [...list.children].find((n) => n.dataset.mid === id);
    const done = (r) => {
      if (opening !== staffOpening || !staffMode) return; // the thread was left or reopened meanwhile
      if (!r || !r.ok) {
        setStatus(staffErrorText(r?.error));
        return;
      }
      node?.remove();
      setStatus('');
      if (!list.children.length) {
        close(true);
        return;
      }
      refreshEmpty();
    };
    Promise.resolve(h.staff.hide(id)).then(done, () => done({ ok: false, error: 'failed' }));
  }

  /** Read the thread with Lumi Staff and draw it; reading is what marks it seen. */
  function loadStaff() {
    if (typeof h.staff?.list !== 'function') return;
    const opening = staffOpening;
    const draw = (r) => {
      if (opening !== staffOpening || !staffMode || !current) return; // an earlier opening, or the thread was left
      if (!r || !r.ok) {
        setStatus(staffErrorText(r?.error));
        return;
      }
      api.onStaffList(r);
    };
    Promise.resolve(h.staff.list()).then(draw, () => draw(null));
  }

  function open(regular) {
    current = regular?.personId || null;
    staffMode = !!regular?.staff;
    staffOpening += 1; // anything still on its way is for the thread that was open before
    handle = regular?.handle || (staffMode ? STAFF_NAME : 'Someone you talked to');
    title.textContent = handle;
    staffBadge.hidden = !staffMode;
    // A thread with staff has no letters and no daily cap, and takes the length staff does.
    letterLabel.hidden = staffMode;
    capEl.hidden = staffMode;
    input.setAttribute('maxlength', staffMode ? String(STAFF_INBOX_MAX_CHARS) : '4000');
    empty.textContent = staffMode ? STAFF_EMPTY_COPY : MESSAGE_REQUEST_COPY;
    list.replaceChildren();
    oldest = null;
    paging = false;
    older.hidden = true;
    input.value = '';
    autosize();
    letter.checked = false;
    emoji.close();
    setStatus('');
    if (staffMode) showCard(false); // no request to explain; a card left up by the last thread must go
    settle();
    root.hidden = false;
    refreshEmpty();
    if (current) {
      if (staffMode) loadStaff();
      else h.onList?.(current);
    }
    input.focus();
  }

  function close(back = false) {
    current = null;
    staffMode = false;
    staffOpening += 1;
    root.hidden = true;
    settle();
    if (back) h.onBack?.();
  }

  const api = {
    root,
    open,
    close,
    /** The personId whose thread is open, or null. */
    current: () => current,
    /** The thread with Lumi Staff was read (HTTP). Ignored unless that thread is the one open. */
    onStaffList(r) {
      if (!staffMode || !current || !r) return false;
      const rows = Array.isArray(r.messages) ? r.messages : [];
      list.replaceChildren(...rows.map(bubble));
      scrollDown();
      older.hidden = true; // the server sends the whole of it
      refreshEmpty();
      return true;
    },
    /**
     * A line from Lumi Staff arrived live (STAFF_MSG). True when the staff
     * thread is the one open and the line is in it; a line the thread already
     * shows (a read that crossed it) is not drawn twice.
     */
    onStaffMsg(m) {
      if (!staffMode || !current || !m || typeof m.text !== 'string') return false;
      if (m.id && [...list.children].some((n) => n.dataset.mid === m.id)) return true;
      list.append(bubble({ ...m, from: 'staff' }));
      scrollDown();
      refreshEmpty();
      return true;
    },
    /** Lumi Staff deleted lines (STAFF_MSG { removed }): they leave the open staff thread. */
    onStaffRemoved(ids) {
      if (!staffMode || !current || !Array.isArray(ids)) return false;
      for (const node of [...list.children]) if (ids.includes(node.dataset.mid)) node.remove();
      refreshEmpty();
      return true;
    },
    /** A MSG_LIST arrived. Ignored unless it is for the open thread. */
    onList(m) {
      if (!current || staffMode || !m || m.with !== current) return false;
      const rows = Array.isArray(m.messages) ? m.messages : [];
      const nodes = rows.map(bubble);
      if (paging) {
        list.prepend(...nodes);
        paging = false;
      } else {
        list.replaceChildren(...nodes);
        scrollDown();
      }
      if (rows.length) oldest = rows[0].at;
      older.hidden = rows.length < THREAD_PAGE;
      if (m.cap) capEl.textContent = capLine(m.cap);
      refreshEmpty();
      return true;
    },
    /** A MSG arrived. True when it belonged to the open thread and was appended. */
    onMsg(m) {
      if (!current || staffMode || !m) return false;
      if (m.from !== current && m.to !== current) return false;
      list.append(bubble(m));
      scrollDown();
      if (m.clientId && m.clientId === pending) {
        input.value = '';
        autosize();
        letter.checked = false;
        settle();
        if (m.slow) setStatus(`Sent as a letter. It arrives in about ${Math.round((Date.parse(m.deliverAt) - Date.parse(m.at)) / 3_600_000)} hours.`);
      }
      refreshEmpty();
      return true;
    },
    /**
     * A MSG_DELETED arrived. Only your OWN deletion removes a bubble here:
     * the other side hiding a message for themselves changes nothing you see.
     */
    onDeleted(m) {
      if (staffMode || !m || !m.id) return false;
      const node = [...list.children].find((n) => n.dataset.mid === m.id);
      if (!node) return false;
      const mine = node.dataset.mine === '1';
      const ours = (mine && m.by === 'sender') || (!mine && m.by === 'recipient');
      if (!ours) return false;
      node.remove();
      refreshEmpty();
      return true;
    },
    /** A SYSTEM notice scoped to messages (a refused message, a thanks for a report). */
    notice(m) {
      if (!current || staffMode || !m) return false;
      settle();
      setStatus(String(m.text || ''));
      return true;
    },
    /** An ERROR while the thread is open. True when it was shown here. */
    error(m) {
      if (!current || staffMode) return false; // a socket's refusal is about a regular; the staff thread says its own
      const text = errorText(m);
      if (!text) return false;
      settle();
      setStatus(text);
      return true;
    },
    setCap(cap) {
      capEl.textContent = capLine(cap);
    },
  };

  return { root, api };
}

export default { createThreadView, capLine, formatArrives, formatWhen, errorText, staffErrorText, hasSeenCard, rememberCard, MESSAGE_REQUEST_COPY, SAFETY_CARD_COPY, LETTER_TOGGLE_COPY, STAFF_EMPTY_COPY };
